// mention_autocomplete.js
// Auto-attaches @mention autocomplete to all Trix editors via trix-initialize.
// When a user types @query, fetches matching team members and lets them insert
// a mention as an ActionText attachment (rendered as a styled chip).

const MENTION_SEARCH_URL = "/api/v1/mentions/search"
const DEBOUNCE_MS = 200
const MIN_QUERY_LENGTH = 1

class MentionAutocomplete {
  constructor(trixEditor) {
    this.trixElement = trixEditor
    this.editor = trixEditor.editor
    this.mentionStart = null
    this.selectedIndex = 0
    this.results = []
    this.debounceTimer = null
    this.isOpen = false

    this.dropdown = this.buildDropdown()
    this.trixElement.parentElement.style.position = "relative"
    this.trixElement.parentElement.appendChild(this.dropdown)

    this.handleInput = this.handleInput.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleClickOutside = this.handleClickOutside.bind(this)

    this.trixElement.addEventListener("trix-change", this.handleInput)
    this.trixElement.addEventListener("keydown", this.handleKeydown)
    document.addEventListener("click", this.handleClickOutside)
  }

  destroy() {
    this.trixElement.removeEventListener("trix-change", this.handleInput)
    this.trixElement.removeEventListener("keydown", this.handleKeydown)
    document.removeEventListener("click", this.handleClickOutside)
    this.dropdown.remove()
    clearTimeout(this.debounceTimer)
  }

  buildDropdown() {
    const el = document.createElement("div")
    el.className = "mention-dropdown"
    el.style.display = "none"
    return el
  }

  // --- Event handlers ---

  handleInput() {
    const position = this.editor.getPosition()
    const text = this.editor.getDocument().toString()
    const beforeCursor = text.substring(0, position)

    // Match @query at end of text before cursor.
    // The @ must be preceded by whitespace/newline or be at position 0.
    const match = beforeCursor.match(/(^|[\s\n])@(\w*)$/)

    if (match) {
      // +1 to skip the whitespace char before @, unless it's start-of-text
      this.mentionStart = position - match[2].length - 1 // points to the @
      const query = match[2]

      if (query.length >= MIN_QUERY_LENGTH) {
        this.debouncedSearch(query)
      } else if (query.length === 0) {
        // Just typed "@" with no query yet — show hint
        this.showDropdown([])
      }
    } else {
      this.hideDropdown()
    }
  }

  handleKeydown(event) {
    if (!this.isOpen) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.results.length - 1)
        this.renderResults()
        break
      case "ArrowUp":
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
        this.renderResults()
        break
      case "Enter":
      case "Tab":
        if (this.results.length > 0) {
          event.preventDefault()
          this.insertMention(this.results[this.selectedIndex])
        }
        break
      case "Escape":
        event.preventDefault()
        this.hideDropdown()
        break
    }
  }

  handleClickOutside(event) {
    if (!this.dropdown.contains(event.target) && !this.trixElement.contains(event.target)) {
      this.hideDropdown()
    }
  }

  // --- Search ---

  debouncedSearch(query) {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.search(query), DEBOUNCE_MS)
  }

  async search(query) {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(
        `${MENTION_SEARCH_URL}?q=${encodeURIComponent(query)}`,
        {
          headers: {
            "Accept": "application/json",
            "X-CSRF-Token": csrfToken || ""
          }
        }
      )

      if (!response.ok) return this.hideDropdown()

      const users = await response.json()
      this.showDropdown(users)
    } catch {
      this.hideDropdown()
    }
  }

  // --- Dropdown rendering ---

  showDropdown(users) {
    this.results = users
    this.selectedIndex = 0
    this.isOpen = true

    this.positionDropdown()
    this.renderResults()
    this.dropdown.style.display = "block"
  }

  hideDropdown() {
    this.isOpen = false
    this.results = []
    this.dropdown.style.display = "none"
    clearTimeout(this.debounceTimer)
  }

  positionDropdown() {
    if (this.mentionStart == null) return

    const rect = this.editor.getClientRectAtPosition(this.mentionStart)
    if (!rect) return

    const editorRect = this.trixElement.getBoundingClientRect()
    const parentRect = this.trixElement.parentElement.getBoundingClientRect()

    // Position below the @ character, relative to the parent container
    this.dropdown.style.top = `${rect.bottom - parentRect.top + 4}px`
    this.dropdown.style.left = `${rect.left - parentRect.left}px`
  }

  renderResults() {
    if (this.results.length === 0) {
      this.dropdown.innerHTML = `
        <div class="mention-dropdown__empty">
          Type a name to mention someone...
        </div>
      `
      return
    }

    this.dropdown.innerHTML = this.results
      .map((user, index) => `
        <button type="button"
                class="mention-dropdown__item ${index === this.selectedIndex ? "mention-dropdown__item--selected" : ""}"
                data-index="${index}">
          <span class="mention-dropdown__avatar">${this.initials(user.name)}</span>
          <span class="mention-dropdown__info">
            <span class="mention-dropdown__name">${this.escapeHtml(user.name)}</span>
            <span class="mention-dropdown__email">${this.escapeHtml(user.email)}</span>
          </span>
        </button>
      `)
      .join("")

    // Bind click handlers
    this.dropdown.querySelectorAll(".mention-dropdown__item").forEach((item) => {
      item.addEventListener("mousedown", (e) => {
        e.preventDefault() // Prevent Trix blur
        const idx = parseInt(item.dataset.index, 10)
        this.insertMention(this.results[idx])
      })
      item.addEventListener("mouseenter", () => {
        this.selectedIndex = parseInt(item.dataset.index, 10)
        this.renderResults()
      })
    })
  }

  // --- Insertion ---

  insertMention(user) {
    const position = this.editor.getPosition()

    // Select the @query text (from @ to cursor)
    this.editor.setSelectedRange([this.mentionStart, position])
    this.editor.deleteInDirection("forward")

    // Insert as an ActionText attachment so Trix preserves it
    const attachment = new Trix.Attachment({
      sgid: user.sgid,
      content: `<span class="mention-chip">@${this.escapeHtml(user.name)}</span>`,
      contentType: "application/vnd.actiontext.mention"
    })
    this.editor.insertAttachment(attachment)
    this.editor.insertString(" ")

    this.hideDropdown()
  }

  // --- Helpers ---

  initials(name) {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .substring(0, 2)
      .toUpperCase()
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}

// --- Global initialization via trix-initialize ---

const mentionInstances = new WeakMap()

document.addEventListener("trix-initialize", (event) => {
  const trixEditor = event.target

  // Avoid double-binding
  if (mentionInstances.has(trixEditor)) return

  const instance = new MentionAutocomplete(trixEditor)
  mentionInstances.set(trixEditor, instance)
})

// Cleanup on Turbo navigation
document.addEventListener("turbo:before-cache", () => {
  document.querySelectorAll("trix-editor").forEach((trixEditor) => {
    const instance = mentionInstances.get(trixEditor)
    if (instance) {
      instance.destroy()
      mentionInstances.delete(trixEditor)
    }
  })
})
